import { nextTick, ref } from 'vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import type { MultiStepFormState, RecordForm, RequestOptions } from '@/types';

/**
 * The row-chevron round trip every records table offers: fetch the registration's formula, load it
 * into the generic `ActionModal` and open that modal on the edit step.
 *
 * No new modal is involved — this is the same `GetRecordForm` → `ActionModal` → `PutRecordForm`
 * path the activity log takes, so a registration is edited through exactly one form wherever it is
 * reached from. The record id changes on every save (the old registration is soft-deleted and a new
 * one chained to it), which is why nothing here is cached across a refetch.
 */
export function useRecordFormModal() {
    const stepConfig = ref<Record<string, unknown>[]>([{}, { deleteButton: true, editable: true }, { submitActionType: ActionType.PutRecordForm }, {}]);

    /**
     * Icon and title of the open form, named after the registration type it edits.
     *
     * Without it the modal opens untitled, which the activity log's copy of this round trip does not
     * do. The log takes the pair from the row it was clicked; a records row carries no type of its
     * own, so it is resolved from the type the form itself reports - the same record-type list the
     * log labels its rows from.
     *
     * Null rather than a pair of empty strings when the type cannot be named: the modal renders its
     * icon for any header object it is given, and an empty `src` makes the browser re-request the
     * page itself as an image and draw a broken-image glyph over the form.
     */
    const header = ref<null | { icon_url: string; text: string }>(null);
    const initialState = ref<MultiStepFormState>({
        location: null,
        form: null,
        confirmed: false,
        farm: null,
        event: null,
        notification: null,
    });

    /** The row whose formula is being fetched, so that row alone can show a spinner. */
    const loadingRecordId = ref<number | null>(null);

    /** `options` rides on both requests, for a caller that reports a failure itself (`skipHandleError`). */
    async function openRecordForm(recordId: number, open: CallableFunction, options?: RequestOptions) {
        if (loadingRecordId.value !== null) {
            return;
        }

        try {
            loadingRecordId.value = recordId;

            // Awaited beside the form rather than fired off at setup: the header is read the moment
            // the form arrives, and a list still in flight would leave the modal untitled. The
            // action is throttled in the store, so a page that already holds the list pays nothing.
            const [recordForm] = await Promise.all([store.dispatch(ActionType.GetRecordForm, { form: { record_id: recordId }, options }) as Promise<RecordForm>, store.dispatch(ActionType.GetRecordTypes, { options })]);

            initialState.value = {
                ...initialState.value,
                form: recordForm.form,
                record_id: recordId,
            };
            stepConfig.value[1].deleteButton = recordForm.deletable;
            stepConfig.value[1].editable = recordForm.editable;

            const recordType = store.state.recordTypes.find((type) => type.id === recordForm.type_id);
            header.value = recordType ? { icon_url: recordType.icon_url, text: recordType.name } : null;

            // The modal reads its state on open, so the state has to be through the DOM first.
            await nextTick();

            open();
        } finally {
            loadingRecordId.value = null;
        }
    }

    return { stepConfig, initialState, header, loadingRecordId, openRecordForm };
}
